import { useNavigate } from "react-router-dom";
import { useLang } from "../../contexts/LangContext";
import { useLogSession } from "../../contexts/LogSessionContext";
import { fishDatabase } from "../../data/fish_db";
import { hasImagesForRecipe } from "../../utils/recipeImageUtils";
import { useEffect, useState } from "react";
import { storageService } from "../../services/storage.service";

export default function WhichRecipe() {
  const { t, lang } = useLang();
  const { newFishItem, setNewFishItem } = useLogSession();
  const navigate = useNavigate();
  const [onlyShowRecipesWithImages, setOnlyShowRecipesWithImages] = useState(true);

  useEffect(() => {
    document.title = "Select recipe - MyAquaFood";
    
    // Load settings
    storageService.getSettings().then(settings => {
      setOnlyShowRecipesWithImages(settings.surveyOnlyShowRecipesWithImages ?? true);
    }).catch(console.error);
  }, []);

  if (!newFishItem) return (
    <div className="mt-8 p-4 text-red-600 px-8 flex flex-col items-center justify-center gap-4">
      <span>{t("error_noFishItem")}</span>
      <button onClick={() => navigate(-1)} className="ml-2 text-blue-600">
        {t("back")}
      </button>
    </div>
  );

  const displayFishName =
    lang === "bn"
      ? newFishItem.fish_local_name || newFishItem.fish_name
      : newFishItem.fish_english_name || newFishItem.fish_name;

  const fish = fishDatabase.find((f) => f.id === newFishItem.fish_id);
  const isOtherFish = newFishItem.fish_id === "other";
  
  // For "Other" fish, show Curry, Fry and Other only
  const fishRecipes = isOtherFish ? ["curry", "fry", "other"] : (fish?.recipes || []);
  const recipes = fishRecipes.includes('other') ? fishRecipes : [...fishRecipes, 'other'];

  const recipeOptions: Array<{
    key: "fry" | "curry" | "roasted" | "smoked" | "grilled" | "mashed" | "other" | "unknown";
    hasImages: boolean;
  }> = recipes.map((recipe) => ({
    key: recipe as "fry" | "curry" | "roasted" | "smoked" | "grilled" | "mashed" | "other" | "unknown",
    hasImages: hasImagesForRecipe(newFishItem.fish_id, recipe),
  })).filter(option => {
    // For "Other" fish, always show all its recipes (curry, fry, other)
    if (isOtherFish) return true;
    // Always show 'other' option
    if (option.key === 'other') return true;
    // If setting is enabled, only show recipes with images
    if (onlyShowRecipesWithImages) {
      return option.hasImages;
    }
    return true;
  });

  const handleSelect = (recipe: typeof newFishItem.recipe) => {
    setNewFishItem({ ...newFishItem, recipe }); 
    navigate("../which-amount");
  };

  return (
    <div className="w-full grow flex flex-col items-center justify-start gap-8 text-center px-scr pt-12">
      <h1 className="text-2xl font-bold text-rose-700">
        {t("whichRecipe_Heading").replace("{fish}", displayFishName)}
      </h1>

      <div className="flex flex-col gap-3 w-full max-w-xs pb-8">
        {recipeOptions.map(({ key, hasImages }) => (
          <button
            key={key}
            onClick={() => handleSelect(key)}
            className="w-full button-base button-bg-grad-light-blue relative"
          >
            <span className="flex items-center justify-center gap-2">
              {t(`whichRecipe_${key}`)}              
            </span>
            {hasImages && (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth="1.5"
                  stroke="currentColor"
                  className="absolute right-6 top-1/2 transform -translate-y-1/2 w-5 h-5 opacity-60"
                  aria-label="Images available"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M6.827 6.175A2.31 2.31 0 0 1 5.186 7.23c-.38.054-.757.112-1.134.175C2.999 7.58 2.25 8.507 2.25 9.574V18a2.25 2.25 0 0 0 2.25 2.25h15A2.25 2.25 0 0 0 21.75 18V9.574c0-1.067-.75-1.994-1.802-2.169a47.865 47.865 0 0 0-1.134-.175 2.31 2.31 0 0 1-1.64-1.055l-.822-1.316a2.192 2.192 0 0 0-1.736-1.039 48.774 48.774 0 0 0-5.232 0 2.192 2.192 0 0 0-1.736 1.039l-.821 1.316Z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M16.5 12.75a4.5 4.5 0 1 1-9 0 4.5 4.5 0 0 1 9 0ZM18.75 10.5h.008v.008h-.008V10.5Z"
                  />
                </svg>
              )}
          </button>
        ))}
      </div>
    </div>
  );
}
